import {
  assertStudioWorkspaceAdmin,
  type ServicePluginContext,
} from "@brains/plugins";
import { mailItemAdapter } from "./entity/adapters/mail-item-adapter";
import {
  mailItemSchema,
  type MailItemEntity,
  type MailStatus,
} from "./entity/schemas/mail-item";
import {
  mailTriageListItemSchema,
  mailTriageListResultSchema,
  mailTriageStatusActionResultSchema,
  mailTriageStatusActionSchema,
  type MailTriageFilter,
  type MailTriageListItem,
  type MailTriageListResult,
  type MailTriageStatusActionResult,
} from "./schemas/operator";
import { definedFields } from "@brains/utils/strip-undefined";

const INBOX_ITEM_LIMIT = 100;

type OperatorContext = Pick<
  ServicePluginContext,
  "entityService" | "permissions"
>;

interface OperatorActor {
  userPermissionLevel?: "admin" | "trusted" | "public" | undefined;
}

export class MailTriageOperatorService {
  private readonly context: OperatorContext;

  constructor(context: OperatorContext) {
    this.context = context;
  }

  async list(filters: MailTriageFilter): Promise<MailTriageListResult> {
    const metadata = metadataFilter(filters);
    const [entities, total] = await Promise.all([
      this.context.entityService.listEntities(
        {
          entityType: "mail-item",
          options: {
            limit: filters.limit,
            sortFields: [{ field: "receivedAt", direction: "desc" }],
            filter: {
              metadata,
              visibilityScope: "restricted",
            },
          },
        },
        mailItemSchema,
      ),
      this.context.entityService.countEntities({
        entityType: "mail-item",
        options: {
          filter: {
            metadata,
            visibilityScope: "restricted",
          },
        },
      }),
    ]);

    return mailTriageListResultSchema.parse({
      items: entities
        .map(toListItem)
        .sort(compareMailItems)
        .slice(0, filters.limit),
      total,
    });
  }

  async listInboxItems(): Promise<MailTriageListItem[]> {
    return (await this.list({ status: "new", limit: INBOX_ITEM_LIMIT })).items;
  }

  async getSourceRef(id: string, actor: OperatorActor): Promise<string> {
    assertMailTriageAdmin(actor);
    const entity = await this.context.entityService.getEntity(
      {
        entityType: "mail-item",
        id,
        visibilityScope: "restricted",
      },
      mailItemSchema,
    );
    if (!entity) throw new Error("Mail item not found");
    const parsedEntity = mailItemSchema.parse(entity);
    return mailItemAdapter.parseMailItemContent(parsedEntity.content)
      .frontmatter.source.ref;
  }

  async act(
    input: unknown,
    actor: OperatorActor,
  ): Promise<MailTriageStatusActionResult> {
    assertMailTriageAdmin(actor);
    const action = mailTriageStatusActionSchema.parse(input);
    this.context.permissions.assertEntityActionAllowed(
      "mail-item",
      "update",
      actor,
    );
    const entity = await this.context.entityService.getEntity(
      {
        entityType: "mail-item",
        id: action.id,
        visibilityScope: "restricted",
      },
      mailItemSchema,
    );
    if (!entity) throw new Error("Mail item not found");

    const parsedEntity = mailItemSchema.parse(entity);
    const { frontmatter, summary } = mailItemAdapter.parseMailItemContent(
      parsedEntity.content,
    );
    const status = statusForAction(action.type);
    assertStatusTransition(frontmatter.status, status);
    if (frontmatter.status === status) {
      return mailTriageStatusActionResultSchema.parse({
        id: action.id,
        status,
      });
    }
    const content = mailItemAdapter.createMailItemContent(
      { ...frontmatter, status },
      summary,
    );
    await this.context.entityService.updateEntity({
      entity: {
        ...parsedEntity,
        content,
        metadata: { ...parsedEntity.metadata, status },
      },
    });

    return mailTriageStatusActionResultSchema.parse({ id: action.id, status });
  }
}

export function assertMailTriageAdmin(actor: OperatorActor): void {
  assertStudioWorkspaceAdmin(actor, "Email triage");
}

function metadataFilter(
  filters: Omit<MailTriageFilter, "limit"> | MailTriageFilter,
): Record<string, unknown> {
  return {
    ...definedFields({
      category: filters.category,
      priority: filters.priority,
      status: filters.status,
    }),
    ...(filters.needsReply !== undefined
      ? { needsReply: filters.needsReply }
      : {}),
  };
}

function toListItem(rawEntity: MailItemEntity): MailTriageListItem {
  const entity = mailItemSchema.parse(rawEntity);
  const { frontmatter, summary } = mailItemAdapter.parseMailItemContent(
    entity.content,
  );
  return mailTriageListItemSchema.parse({
    id: entity.id,
    title: frontmatter.title,
    category: frontmatter.category,
    priority: frontmatter.priority,
    status: frontmatter.status,
    needsReply: frontmatter.needsReply,
    receivedAt: frontmatter.receivedAt,
    summary,
    ...(frontmatter.senderLabel
      ? { senderLabel: frontmatter.senderLabel }
      : {}),
    ...(frontmatter.source.personId
      ? { personId: frontmatter.source.personId }
      : {}),
    ...(frontmatter.source.threadOrdinal !== undefined
      ? { threadOrdinal: frontmatter.source.threadOrdinal }
      : {}),
    ...(frontmatter.organization
      ? { organization: frontmatter.organization }
      : {}),
    requestedActions: frontmatter.requestedActions,
  });
}

function compareMailItems(
  left: MailTriageListItem,
  right: MailTriageListItem,
): number {
  return (
    right.receivedAt.localeCompare(left.receivedAt) ||
    left.id.localeCompare(right.id)
  );
}

function assertStatusTransition(from: MailStatus, to: MailStatus): void {
  const allowed: Record<MailStatus, MailStatus[]> = {
    new: ["reviewed", "handled", "archived"],
    reviewed: ["handled", "archived"],
    handled: ["archived"],
    archived: [],
  };
  if (from !== to && !allowed[from].includes(to)) {
    throw new Error("Invalid mail item status transition");
  }
}

function statusForAction(
  type: "mark-reviewed" | "mark-handled" | "archive",
): MailStatus {
  switch (type) {
    case "mark-reviewed":
      return "reviewed";
    case "mark-handled":
      return "handled";
    case "archive":
      return "archived";
  }
}
